import type { BadgeProps } from "@/components/ui";
import type { MarketplaceCampaignStatus } from "@/hooks/useMarketplaceCampaigns";
import type {
  ApplicationStatus,
  CollaborationStatus,
  DeliverableStatus,
} from "@/hooks/useCollaborations";

/**
 * One place that decides what colour a status is.
 *
 * There used to be three of these maps — `campaignConstants.ts`,
 * `CampaignApplications.tsx` and `CollaborationsInbox.tsx` — each written in raw
 * Tailwind literals and each disagreeing with the others. The inbox coloured
 * `PUBLISHED` emerald, which told a brand the job was finished at the exact
 * moment it still owed a verification.
 *
 * Semantic colour in this system means a money state and nothing else:
 *
 *   success  paid, verified, released — the work is done and settled
 *   accent   in motion, ours to move on
 *   warning  waiting on someone, or declared but unchecked
 *   changes  revisions requested
 *   danger   cancelled with a dispute, or rejected
 *   neutral  not live yet, or closed without incident
 *
 * `PUBLISHED` is warning, never success. It is the creator's claim that a post
 * is live; `VERIFIED`/`RELEASED` is that claim checked, and only the second one
 * is green.
 */
type Tone = NonNullable<BadgeProps["variant"]>;

export const CAMPAIGN_TONE: Record<MarketplaceCampaignStatus, Tone> = {
  DRAFT: "neutral",
  OPEN: "success",
  IN_PROGRESS: "accent",
  COMPLETED: "neutral",
  CANCELLED: "danger",
};

export const APPLICATION_TONE: Record<ApplicationStatus, Tone> = {
  PENDING: "neutral",
  SHORTLISTED: "warning",
  ACCEPTED: "success",
  REJECTED: "danger",
  WITHDRAWN: "neutral",
};

export const COLLABORATION_TONE: Record<CollaborationStatus, Tone> = {
  NEGOTIATING: "warning",
  AGREED: "accent",
  FUNDED: "accent",
  SHIPPED: "accent",
  IN_PROGRESS: "accent",
  SUBMITTED: "warning",
  REVISION_REQUESTED: "changes",
  APPROVED: "accent",
  // A claim, not a completion. See the note above.
  PUBLISHED: "warning",
  RELEASED: "success",
  DISPUTED: "danger",
  CANCELLED: "neutral",
};

export const DELIVERABLE_TONE: Record<DeliverableStatus, Tone> = {
  PENDING: "neutral",
  SUBMITTED: "warning",
  REVISION_REQUESTED: "changes",
  APPROVED: "accent",
  PUBLISHED: "accent",
  VERIFIED: "success",
};
